<script lang="ts" setup>
    import type { LeaderboardEntry, LeaderboardResponse } from '~/types/scoring/Leaderboard';
    import type { RankingGameColumn } from './RankingGameColumn';

    const props = defineProps<{
        highscores: LeaderboardResponse | null;
        gameColumns: RankingGameColumn[];
    }>();

    const { t } = useI18n();

    const showAll = ref(false);
    const MAX_VISIBLE_TOP = 3;

    type DisplayedRow =
        | {
              kind: 'entry';
              game: LeaderboardEntry;
              id: string;
          }
        | {
              kind: 'ellipsis';
              id: string;
              isToggle?: boolean;
          };

    const getGameScore = (entry: LeaderboardEntry, gameId: string) => {
        return entry.gameScores.find((item) => item.gameId === gameId)?.score ?? 0;
    };

    const displayedRows = computed<DisplayedRow[]>(() => {
        const entries = props.highscores?.topScores || [];

        if (entries.length === 0) {
            return [];
        }

        const hasMoreThanTop = entries.length > MAX_VISIBLE_TOP;

        if (showAll.value) {
            const rows: DisplayedRow[] = entries.map((game, index) => ({
                kind: 'entry',
                game,
                id: `entry-${index}`
            }));

            if (hasMoreThanTop) {
                rows.push({ kind: 'ellipsis', id: 'ellipsis-collapse', isToggle: true });
            }

            return rows;
        }

        if (!hasMoreThanTop) {
            return entries.map((game, index) => ({
                kind: 'entry',
                game,
                id: `entry-${index}`
            }));
        }

        const indicesToShow = new Set<number>();
        for (let i = 0; i < Math.min(MAX_VISIBLE_TOP, entries.length); i++) {
            indicesToShow.add(i);
        }

        const currentIndex = entries.findIndex((score) => score.isCurrentUser);
        if (currentIndex !== -1) {
            for (let offset = -1; offset <= 1; offset++) {
                const idx = currentIndex + offset;
                if (idx >= 0 && idx < entries.length) {
                    indicesToShow.add(idx);
                }
            }
        }

        const sortedIndices = Array.from(indicesToShow).sort((a, b) => a - b);

        const rows: DisplayedRow[] = [];
        let lastIndex = -1;

        sortedIndices.forEach((index) => {
            if (lastIndex !== -1 && index - lastIndex > 1) {
                rows.push({ kind: 'ellipsis', id: `ellipsis-${lastIndex}-${index}` });
            }

            if (!entries[index]) return;

            rows.push({
                kind: 'entry',
                game: entries[index],
                id: `entry-${index}`
            });

            lastIndex = index;
        });

        if (lastIndex < entries.length - 1) {
            rows.push({ kind: 'ellipsis', id: `ellipsis-end-${lastIndex}`, isToggle: true });
        }

        return rows;
    });

    const toggleLabel = computed(() => (showAll.value ? t('ranking.actions.collapse') : t('ranking.actions.expand')));

    const colSpan = computed(() => 3 + props.gameColumns.length);
</script>

<template>
    <div class="ranking-table">
        <div class="ranking-table__surface">
            <table
                v-if="displayedRows.length"
                class="table"
            >
                <thead>
                    <tr>
                        <th>{{ $t('ranking.table.rank') }}</th>
                        <th>{{ $t('ranking.table.nickname') }}</th>
                        <th
                            v-for="column in props.gameColumns"
                            :key="column.gameId"
                        >
                            {{ column.label }}
                        </th>
                        <th>{{ $t('ranking.table.totalScore') }}</th>
                    </tr>
                </thead>

                <tbody>
                    <template
                        v-for="row in displayedRows"
                        :key="row.id"
                    >
                        <tr
                            v-if="row.kind === 'entry'"
                            :class="{ 'current-user': row.game.isCurrentUser }"
                        >
                            <td>{{ row.game.rank }}</td>
                            <td>
                                {{ row.game.nickname || $t('ranking.anonymous') }}
                                {{ row.game.isCurrentUser ? ` ${$t('ranking.salutation')}` : '' }}
                            </td>
                            <td
                                v-for="column in props.gameColumns"
                                :key="column.gameId"
                            >
                                {{ getGameScore(row.game, column.gameId) }}
                            </td>
                            <td>{{ row.game.score }}</td>
                        </tr>

                        <tr
                            v-else
                            class="ellipsis-row"
                        >
                            <td :colspan="colSpan">
                                <PrimaryButton
                                    v-if="row.isToggle"
                                    size="small"
                                    class="table__toggle-inline"
                                    :native-button="true"
                                    :aria-expanded="showAll"
                                    :retain-size="true"
                                    :button-text="toggleLabel"
                                    @click="showAll = !showAll"
                                />
                                <span v-else>...</span>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/pta-family/memory-game/ranking-table.scss';
</style>
